package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Immutable
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp

/**
 * Centralized sizing tokens — icon sizes, border/stroke widths, touch targets, etc.
 * (docs/DESIGN_SYSTEM.md). Access via `AppTheme.sizes`. NEVER use raw `.dp` for these in any
 * component or screen — add a token here and reference it, so a single change updates everywhere.
 */
@Immutable
data class AppSizes(
    val iconSm: Dp = 16.dp,
    val iconButton: Dp = 20.dp,
    val iconMd: Dp = 24.dp,
    val iconLg: Dp = 32.dp,
    val iconHero: Dp = 64.dp,
    val brandMark: Dp = 96.dp, // Mahaleh logo mark on the welcome/splash tile
    val gemWatermark: Dp = 148.dp, // oversized faded ornament behind a Hidden Gems story card
    val gemGlassButton: Dp = 44.dp, // frosted circular action button on a gem story card
    val categoryTile: Dp = 48.dp,
    val categoryBadge: Dp = 56.dp, // circular icon badge on the All-Categories cards (Stitch w-14)
    val homeCategoryTile: Dp = 64.dp,
    /** Square tile in the profile photo gallery strip (ProfilePhotoStrip). */
    val profilePhotoTile: Dp = 112.dp,
    val featuredCardWidth: Dp = 280.dp,
    val eventRailCardWidth: Dp = 248.dp, // compact EventRailCard in the Featured / I'm-going rails
    val compactItemCardWidth: Dp = 200.dp, // horizontal "More from this seller" strip cards
    val railItemCardWidth: Dp = 156.dp, // compact MarketItemRailCard in Saved/Featured/seller rails
    val listThumbnail: Dp = 64.dp,
    val resultImageHeight: Dp = 128.dp,
    // Caps the illustrated category motif inside a no-photo BusinessCover so it stays a centered
    // emblem on large surfaces (the ~700dp detail hero) instead of a blown-up full-bleed emoji.
    val coverMotifMax: Dp = 148.dp,
    val iconCircle: Dp = 40.dp,
    val avatarSm: Dp = 32.dp,
    val avatarMd: Dp = 48.dp,
    val avatarLg: Dp = 56.dp,
    val avatarXl: Dp = 112.dp, // person-profile hero portrait
    val notificationBadgeMinSize: Dp = 20.dp,
    val skeletonLine: Dp = 14.dp, // shimmer placeholder for a body-text line
    val skeletonTitle: Dp = 20.dp, // shimmer placeholder for a section/card title line
    val reviewCommentMinHeight: Dp = 120.dp,
    val borderWidth: Dp = 1.dp,
    val strokeSm: Dp = 2.dp,
    val minTouchTarget: Dp = 48.dp,
    val chatBubbleMaxWidth: Dp = 280.dp,
    val commentsListMaxHeight: Dp = 320.dp,
    val editorSheetMaxHeight: Dp = 560.dp, // scrollable cap for the full profile editor sheet
    val qrCode: Dp = 220.dp,
    val chartHeight: Dp = 180.dp,
    val chartStroke: Dp = 2.dp,
    val strengthBar: Dp = 4.dp, // password-strength meter segment height
    val chartLegendDot: Dp = 8.dp,
    val contentMaxWidthCompact: Dp = 448.dp,
    // Cap for reading-comfortable content on wide/desktop web. 1280 let content sprawl nearly edge-to-edge
    // on a laptop; ~1120 keeps line lengths and card grids comfortable while still using the space.
    val contentMaxWidth: Dp = 1120.dp,
    // Narrower cap for long-form text (Terms, policies): ~720 keeps line length readable (~90 chars).
    val readingMaxWidth: Dp = 720.dp,
    // Cap for segmented choice controls (language, appearance). They divide their width equally, so on
    // a desktop-width card each of three segments ballooned to ~380dp — a button the size of a hero
    // CTA for picking "Dark". Capped, they stay a control instead of becoming the layout.
    val segmentedMaxWidth: Dp = 420.dp,
    val pageIndicatorDot: Dp = 8.dp, // pager dots (onboarding carousel)
    val pageIndicatorActiveWidth: Dp = 24.dp, // active pager dot stretches into a pill
    val illustrationTile: Dp = 88.dp, // flat icon tiles inside onboarding illustration collages
    val navCenterAction: Dp = 56.dp, // raised circular Post button floating over the bottom nav
    // Cook Mode Previous/Next paddles (Stitch a0906c31): oversized so wet/floury hands hit them.
    val cookModePaddle: Dp = 64.dp,
    // Bottom padding a scrolling screen adds so its last items clear the bottom nav bar + a
    // floating action button hovering above it.
    val bottomBarContentClearance: Dp = 96.dp,
    val navRailWidth: Dp = 256.dp,
    val tourCutoutRadius: Dp = 16.dp, // rounded corners of the feature-tour spotlight cutout
    val tourCardMaxWidth: Dp = 360.dp, // feature-tour tooltip card cap (readable on wide/desktop)
    val topNavBarHeight: Dp = 80.dp, // desktop/web top navigation bar (Stitch h-20 = 80px)
    val topNavActiveUnderline: Dp = 2.dp, // gold active-tab underline (Stitch border-b-2)
    val topNavBrandMark: Dp = 44.dp, // leaf logo beside the wordmark in the desktop/web top bar
)

val appSizes: AppSizes = AppSizes()
